// MUI Imports
import type { Theme } from '@mui/material/styles'

// Type Imports
import type { Settings } from '@/stores/settings'
import type { SystemMode, Skin } from '@core/types'

// Config Imports
import themeSettings from '@configs/theme'

// Theme Options Imports
import overrides from './overrides'
import colorSchemes from './color-schemes'
import spacing from './spacing'
import shadows from './shadows'
import customShadows from './custom-shadows'
import typography from './typography'

// The families come from `src/configs/theme.ts`; the font files themselves
// are imported in `src/main.tsx` (in Next this was `next/font/google`).
const fontFamily = themeSettings.typography.fontFamily
const displayFontFamily = themeSettings.typography.displayFontFamily

// The five radius steps orbit the base radius, from 4px below it to 4px above
// — so changing `shape.borderRadius` in the theme moves the whole scale.
const borderRadius = themeSettings.shape.borderRadius
const step = (delta: number) => Math.max(0, borderRadius + delta)

const theme = (settings: Settings, mode: SystemMode, direction: Theme['direction']): Theme => {
  return {
    direction,
    components: overrides(settings.skin as Skin),
    colorSchemes: colorSchemes(settings.skin as Skin),
    ...spacing,
    shape: {
      borderRadius,
      customBorderRadius: {
        xs: step(-4),
        sm: step(-2),
        md: step(0),
        lg: step(2),
        xl: step(4)
      }
    },
    shadows: shadows(mode),
    typography: typography(fontFamily, displayFontFamily),
    customShadows: customShadows(mode),
    mainColorChannels: {
      // The brand navy, as channels: text, dividers and every action tone are
      // this color at some opacity, so the page belongs to the mark.
      light: '29 45 80',
      dark: '222 230 244',
      lightShadow: '29 45 80',
      darkShadow: '10 17 33'
    }
  } as Theme
}

export default theme
